import { useEffect, useRef } from 'react';

/** Vivos sobre navy y sobre la tarjeta blanca del premio. */
const COLORS = [
  '#FF4343', // rojo del logotipo
  '#F0645A', // coral de marca
  '#FFC24B', // oro
  '#3FC77E', // verde
  '#42A5E8', // azul
  '#A97BF0', // lila
  '#FFFFFF',
];

type Shape = 'rect' | 'ribbon' | 'disc';

interface Piece {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  w: number;
  h: number;
  color: string;
  shape: Shape;
  phase: number;
  flutter: number;
}

const GRAVITY = 0.3;
const DRAG = 0.991;
const LIFE_MS = 4200;
const FADE_FROM = 3100;

/**
 * Dos canones desde las esquinas inferiores, disparando hacia dentro y arriba.
 * Es mas vistoso que la lluvia vertical y, sobre todo, deja el centro
 * despejado: si el confeti sale del medio, tapa el texto del premio.
 */
function makePiece(cx: number, cy: number, baseAngle: number, spread: number): Piece {
  const angle = baseAngle + (Math.random() - 0.5) * spread;
  const speed = 17 + Math.random() * 15;
  const shape: Shape = Math.random() < 0.18 ? 'disc' : Math.random() < 0.4 ? 'ribbon' : 'rect';
  const size = 8 + Math.random() * 8;
  return {
    x: cx + (Math.random() - 0.5) * 40,
    y: cy + (Math.random() - 0.5) * 40,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    rot: Math.random() * Math.PI * 2,
    vr: (Math.random() - 0.5) * 0.32,
    w: shape === 'ribbon' ? size * 0.5 : size,
    h: shape === 'ribbon' ? size * 2.1 : size * 1.35,
    color: COLORS[Math.floor(Math.random() * COLORS.length)]!,
    shape,
    phase: Math.random() * Math.PI * 2,
    flutter: 0.09 + Math.random() * 0.13,
  };
}

interface Props {
  /** Cambiar este numero lanza una tanda nueva. 0 = no lanzar nada. */
  fire: number;
  /** Cuantas piezas. El premio gordo merece mas. */
  intensity?: 'normal' | 'jackpot';
}

export function Confetti({ fire, intensity = 'normal' }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!fire) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let heightPx = 0;
    const resize = () => {
      width = canvas.clientWidth;
      heightPx = canvas.clientHeight;
      canvas.width = width * dpr;
      canvas.height = heightPx * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const perCannon = intensity === 'jackpot' ? 115 : 70;
    const SPREAD = Math.PI * 0.42;
    const cannons: [number, number, number][] = [
      [width * 0.04, heightPx * 0.9, -Math.PI * 0.3], // izquierda, hacia arriba-derecha
      [width * 0.96, heightPx * 0.9, -Math.PI * 0.7], // derecha, hacia arriba-izquierda
    ];
    const salvo = (n: number) =>
      cannons.flatMap(([x, y, a]) => Array.from({ length: n }, () => makePiece(x, y, a, SPREAD)));

    const pieces = salvo(perCannon);

    // Segunda tanda, para que la celebracion no se apague de golpe.
    let secondWave = intensity === 'jackpot' ? Math.round(perCannon * 0.55) : 0;

    let raf = 0;
    const started = performance.now();

    const tick = (t: number) => {
      const elapsed = t - started;
      ctx.clearRect(0, 0, width, heightPx);
      ctx.globalAlpha = elapsed > FADE_FROM ? Math.max(0, 1 - (elapsed - FADE_FROM) / (LIFE_MS - FADE_FROM)) : 1;

      if (secondWave && elapsed > 420) {
        pieces.push(...salvo(secondWave));
        secondWave = 0;
      }

      for (const p of pieces) {
        p.vy += GRAVITY;
        p.vx *= DRAG;
        p.vy *= DRAG;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.phase += p.flutter;

        if (p.y - p.h > heightPx) continue;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === 'disc') {
          ctx.beginPath();
          ctx.ellipse(0, 0, p.w / 2, (p.h / 2) * Math.abs(Math.cos(p.phase)), 0, 0, Math.PI * 2);
          ctx.fill();
        } else {
          // El escalado en Y simula el papelito girando sobre su eje.
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * Math.abs(Math.cos(p.phase)));
        }
        ctx.restore();
      }

      if (elapsed < LIFE_MS) raf = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, width, heightPx);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      ctx.clearRect(0, 0, width, heightPx);
    };
  }, [fire, intensity]);

  return <canvas className="confetti-layer" ref={canvasRef} aria-hidden="true" />;
}
